import React from 'react';
import { Accordion, Title, SubTitle } from 'shared/ui';
import { Container, Content, Description, FaqList, FaqItem, AccordionContent } from './FAQSection.style';

export const faqList = [
    {
        title: 'How do I verify a phone number for free?',
        content:
            'Enter any phone number above and PhoneCheckr instantly returns the carrier name, line type, and validity. Single lookups are free (up to 10 per hour). For bulk verification, upload a CSV or Excel file and validate your entire list at $0.01 per number. No subscription required.',
    },
    {
        title: 'Can PhoneCheckr detect VOIP phone numbers?',
        content:
            'Yes. PhoneCheckr detects VOIP numbers (internet-based phone numbers from providers like Google Voice, Twilio, and Vonage) and flags them separately from mobile and landline numbers. This lets you filter VOIP numbers before running SMS campaigns or importing contacts.',
    },
    {
        title: 'How do I look up the carrier for a phone number?',
        content:
            'PhoneCheckr queries a national phone database and returns the carrier name (e.g., Verizon, AT&T, T-Mobile) and line type for any US or Canadian number. Enter a single number above for an instant result, or upload a CSV to get carrier data for every number in bulk.',
    },
    {
        title: "What's the difference between mobile, landline, and VOIP numbers?",
        content:
            'Mobile numbers are assigned to cell phones and can receive SMS messages. Landline numbers are tied to a fixed location and cannot receive texts. VOIP (Voice over IP) numbers are internet-based and may or may not support SMS depending on the provider. Knowing the line type helps you choose the right outreach channel.',
    },
    {
        title: 'How can I find if the phone numbers are mobile or landline?',
        content:
            'PhoneCheckr detects whether a phone number is mobile, landline, or VOIP by checking a national carrier database. It returns carrier name, line type, and validity for every number, so you can route outreach to the right channel.',
    },
    {
        title: 'Can I upload a CSV or Excel file to check phone numbers in bulk?',
        content:
            'Yes. Upload a CSV or Excel file, map the column containing your numbers, and PhoneCheckr validates and enriches every entry at $0.01 per number. Download the results as a report showing carrier name, line type (mobile, landline, or VOIP), and validity for each number.',
    },
    {
        title: 'Can I use PhoneCheckr to clean my phone list before sending text messages?',
        content:
            'Yes. Removing landlines, VOIP numbers, and invalid entries before an SMS campaign reduces wasted spend and improves deliverability. PhoneCheckr identifies which numbers are mobile and reachable by SMS so you can clean your list before sending.',
    },
    {
        title: 'Is phone number verification free?',
        content:
            'Individual lookups and basic validation (including country code, number format, and line type) are free. Full carrier reports for bulk CSV uploads are available at $0.01 per lookup, with no subscription required.',
    },
    {
        title: "How accurate is PhoneCheckr's phone number lookup?",
        content:
            'PhoneCheckr queries a national phone database to determine carrier and line type. Accuracy is high for active numbers in the US and Canada. Numbers recently ported between carriers may have a brief lag before the database reflects the change.',
    },
    {
        title: 'Which payment methods do you accept?',
        content:
            'We use Stripe for secure payment processing and accept VISA, Mastercard, American Express, and Discover debit and credit cards.',
    },
    {
        title: 'Is PhoneCheckr a free phone number lookup tool?',
        content:
            'PhoneCheckr offers free single lookups (up to 10 per hour). Enter any US or Canadian number above and instantly see the carrier name, line type (mobile, landline, or VOIP), and whether the number is valid. No account required. Bulk CSV lookups are $0.01 per number with no subscription.',
    },
    {
        title: 'What is bulk phone number validation and what does it include?',
        content:
            'Bulk phone number validation is the process of checking a large list of phone numbers at once, verifying each one for format, validity, country code, carrier name, and line type. Upload a CSV or Excel file to PhoneCheckr, select the column containing your numbers, and download a cleaned, enriched report. Bulk lookups are $0.01 per number with no subscription required.',
    },
    {
        title: 'How does phone number verification improve SMS deliverability?',
        content:
            'SMS deliverability improves when you only send to verified mobile numbers. Phone number verification identifies landline and VOIP numbers that cannot receive text messages so you can remove them before your campaign. Filtering to mobile-only numbers reduces bounce rates, avoids carrier filtering, and lowers your cost per delivered message. Phone number verification is a standard step before any SMS send.',
    },
];

const FaqSection: React.FC = () => {
    return (
        <Container id="section-faqs">
            <Content>
                <Description>
                    <Title as="h2" text="Frequently asked questions" margin="0 0 8px 0" />
                    <SubTitle text="Have questions? We're here to help." />
                </Description>
                <FaqList>
                    {faqList.map(({ title, content }) => {
                        return (
                            <FaqItem key={title}>
                                <Accordion title={title}>
                                    <AccordionContent>{content}</AccordionContent>
                                </Accordion>
                            </FaqItem>
                        );
                    })}
                </FaqList>
            </Content>
        </Container>
    );
};

export default FaqSection;
